import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/useToast'
import { voteOnMastodonPoll } from '@/services/mastodonService'
import type { Post, PostPoll, PostsPage } from '@/types/posts'
import type { Thread, ThreadPost } from '@/types/thread'

interface VoteOnPollArgs {
    post: Post
    choices: number[]
}

/**
 * React Query mutation hook for voting on a Mastodon poll.
 *
 * Optimistically updates the post.poll field in all relevant caches
 * (feeds, threads, single-post) to show voted state immediately.
 */
export function usePollVote() {
    const { mastodonSession } = useAuth()
    const { showToast } = useToast()
    const queryClient = useQueryClient()

    const updatePollInCache = (postUri: string, updater: (poll: PostPoll) => PostPoll) => {
        const updatePost = (post: Post): Post => {
            if (post.uri === postUri && post.poll) {
                return { ...post, poll: updater(post.poll) }
            }
            return post
        }

        // Update infinite feed caches
        queryClient.setQueriesData<InfiniteData<PostsPage>>({ queryKey: ['posts'] }, oldData => {
            if (!oldData?.pages) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    posts: page.posts.map(updatePost),
                })),
            }
        })

        // Update timeline caches
        queryClient.setQueriesData<InfiniteData<PostsPage>>({ queryKey: ['timeline'] }, oldData => {
            if (!oldData?.pages) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page => ({
                    ...page,
                    posts: page.posts.map(updatePost),
                })),
            }
        })

        // Update thread caches
        const updateThreadData = (oldThread: Thread | undefined) => {
            if (!oldThread) return oldThread
            const updateThreadPost = (tp: ThreadPost): ThreadPost =>
                tp.post.uri === postUri && tp.post.poll ? { ...tp, post: updatePost(tp.post) } : tp
            return {
                ...oldThread,
                post: updateThreadPost(oldThread.post),
                parentChain: oldThread.parentChain.map(updateThreadPost),
                replies: oldThread.replies.map(updateThreadPost),
            }
        }
        queryClient.setQueriesData<Thread>({ queryKey: ['thread'] }, updateThreadData)
        queryClient.setQueriesData<Thread>({ queryKey: ['ap-thread'] }, updateThreadData)

        // Update single-post cache
        queryClient.setQueriesData<Post>({ queryKey: ['post', postUri] }, oldPost =>
            oldPost?.poll ? updatePost(oldPost) : oldPost
        )
    }

    const voteMutation = useMutation({
        mutationFn: async ({ post, choices }: VoteOnPollArgs) => {
            if (!mastodonSession) throw new Error('Not authenticated with Mastodon')
            if (!post.poll) throw new Error('Post has no poll')
            return voteOnMastodonPoll(mastodonSession, post.uri, choices)
        },
        onMutate: ({ post, choices }) => {
            if (!post.poll) return
            // Snapshot current cache state before optimistic update for rollback
            const previousPostsData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: ['posts'],
            })
            const previousTimelineData = queryClient.getQueriesData<InfiniteData<PostsPage>>({
                queryKey: ['timeline'],
            })
            const previousThreadData = queryClient.getQueriesData<Thread>({ queryKey: ['thread'] })
            const previousApThreadData = queryClient.getQueriesData<Thread>({
                queryKey: ['ap-thread'],
            })
            const previousPostData = queryClient.getQueriesData<Post>({
                queryKey: ['post', post.uri],
            })

            // Optimistic update: mark as voted with chosen indices
            updatePollInCache(post.uri, poll => ({
                ...poll,
                voted: true,
                ownVotes: choices,
            }))

            return {
                previousPostsData,
                previousTimelineData,
                previousThreadData,
                previousApThreadData,
                previousPostData,
            }
        },
        onSuccess: (updatedPoll, { post }) => {
            // Replace optimistic data with server response
            updatePollInCache(post.uri, () => updatedPoll)
            // Cancel any in-flight thread refetches so a stale response from the
            // unauthenticated origin instance (which returns voted: null) can't
            // overwrite the voted state we just set.
            void queryClient.cancelQueries({ queryKey: ['thread'] })
            void queryClient.cancelQueries({ queryKey: ['ap-thread'] })
        },
        onError: (error, _vars, context) => {
            console.error('[usePollVote] vote failed:', error)
            // Roll back optimistic update using pre-mutation snapshots
            if (context) {
                context.previousPostsData.forEach(([queryKey, data]) =>
                    queryClient.setQueryData(queryKey, data)
                )
                context.previousTimelineData.forEach(([queryKey, data]) =>
                    queryClient.setQueryData(queryKey, data)
                )
                context.previousThreadData.forEach(([queryKey, data]) =>
                    queryClient.setQueryData(queryKey, data)
                )
                context.previousApThreadData.forEach(([queryKey, data]) =>
                    queryClient.setQueryData(queryKey, data)
                )
                context.previousPostData.forEach(([queryKey, data]) =>
                    queryClient.setQueryData(queryKey, data)
                )
            }
            showToast('Failed to submit vote', 'error')
        },
    })

    return {
        vote: (post: Post, choices: number[]) => voteMutation.mutate({ post, choices }),
        isVoting: voteMutation.isPending,
    }
}
